import type { ClientMsg, ServerMsg } from '@billycheckers/shared';

const TOKEN_KEY = 'bc:token';
const NAME_KEY = 'bc:name';

export type NetStatus = 'connecting' | 'online' | 'offline';

/**
 * WS-клиент: автопереподключение с экспоненциальным backoff (макс. 10 с),
 * сторожок тишины 40 с, hello при каждом открытии.
 */
export class Net {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private lastMsgAt = Date.now();
  private reconnectTimer: number | null = null;
  private closed = false;
  private connectStartedAt = 0;

  onMessage: (msg: ServerMsg) => void = () => {};
  onStatus: (s: NetStatus) => void = () => {};
  /** Вызывается после welcome — здесь приложение восстанавливает подписки/комнату. */
  onReady: () => void = () => {};

  constructor() {
    window.setInterval(() => this.watchdog(), 5000);
    window.setInterval(() => this.send({ t: 'ping' }), 20_000);
  }

  get token(): string | null {
    // sessionStorage позволяет вкладке сохранять СВОЮ идентичность при перезагрузке,
    // даже если в другой вкладке этого браузера играет другой человек
    return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
  }

  get name(): string {
    return localStorage.getItem(NAME_KEY) ?? '';
  }

  set name(v: string) {
    localStorage.setItem(NAME_KEY, v);
  }

  connect(): void {
    this.closed = false;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.onStatus(this.attempts === 0 ? 'connecting' : 'offline');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    this.connectStartedAt = Date.now();
    ws.onopen = () => {
      this.attempts = 0;
      this.lastMsgAt = Date.now();
      const token = this.token;
      this.sendNow({ t: 'hello', name: this.name || 'Гость', ...(token ? { token } : {}) });
    };
    ws.onmessage = (ev) => {
      this.lastMsgAt = Date.now();
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMsg;
      } catch {
        return;
      }
      if (msg.t === 'welcome') {
        localStorage.setItem(TOKEN_KEY, msg.token);
        sessionStorage.setItem(TOKEN_KEY, msg.token);
        this.onStatus('online');
        this.onReady();
      }
      this.onMessage(msg);
    };
    ws.onclose = () => {
      this.ws = null;
      if (this.closed) return;
      this.onStatus('offline');
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = Math.min(10_000, 500 * 2 ** this.attempts);
    this.attempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private watchdog(): void {
    if (!this.ws) return;
    // тишина 40 с на открытом соединении → пересоединиться
    if (this.ws.readyState === WebSocket.OPEN && Date.now() - this.lastMsgAt > 40_000) {
      this.ws.close(); // onclose запустит переподключение
      return;
    }
    // зависший CONNECTING («чёрная дыра») — не ждать таймаут браузера
    if (this.ws.readyState === WebSocket.CONNECTING && Date.now() - this.connectStartedAt > 10_000) {
      this.ws.close();
    }
  }

  send(msg: ClientMsg): void {
    this.sendNow(msg);
  }

  private sendNow(msg: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return;
    }
    // соединения нет — не молчать: сигналим и форсируем переподключение;
    // после reconnect сервер пришлёт полный room:state (пересинхронизация)
    if (msg.t === 'ping') return;
    this.onStatus('offline');
    if (this.ws) this.ws.close();
    else if (!this.closed) this.scheduleReconnect();
  }
}
